import { create } from 'zustand';
import { api } from '@/lib/api';

interface AnalyticsOverview {
  totalPageviews: number;
  uniqueVisitors: number;
  formSubmissions: number;
  avgPagesPerSession: string;
}

interface PageviewByDay {
  date: string;
  count: number;
}

interface TopPage {
  path: string;
  count: number;
}

interface TopReferrer {
  referrer: string;
  count: number;
}

interface TopCountry {
  country: string;
  count: number;
}

interface DeviceBreakdown {
  desktop: number;
  mobile: number;
  tablet: number;
}

interface RealtimeData {
  activeVisitors: number;
  recentPages: {
    path: string;
    sessionId: string;
    country: string | null;
  }[];
}

interface AnalyticsData {
  overview: AnalyticsOverview;
  pageviewsByDay: PageviewByDay[];
  topPages: TopPage[];
  topReferrers: TopReferrer[];
  topCountries: TopCountry[];
  devices: DeviceBreakdown;
  period: string;
}

interface AnalyticsState {
  data: AnalyticsData | null;
  realtime: RealtimeData | null;
  isLoading: boolean;
  period: '7d' | '30d' | '90d';

  fetchAnalytics: (siteId: string) => Promise<void>;
  fetchRealtime: (siteId: string) => Promise<void>;
  setPeriod: (period: '7d' | '30d' | '90d') => void;
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  data: null,
  realtime: null,
  isLoading: false,
  period: '7d',

  fetchAnalytics: async (siteId: string) => {
    set({ isLoading: true });
    try {
      const period = get().period;
      const response = await api.get<AnalyticsData>(
        `/analytics/sites/${siteId}/overview?period=${period}`
      );
      set({ data: response });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      set({ isLoading: false });
    }
  },

  fetchRealtime: async (siteId: string) => {
    try {
      const response = await api.get<RealtimeData>(`/analytics/sites/${siteId}/realtime`);
      set({ realtime: response });
    } catch (error) {
      console.error('Failed to fetch realtime data:', error);
    }
  },

  setPeriod: (period) => {
    set({ period });
  },
}));
